/**
 * Cliente Form Dialog Component
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Cliente } from "@/types/cliente";
import { clienteSchema, ClienteFormData } from "@/schemas/clienteSchema";

interface ClienteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  cliente: Cliente | null;
  onSubmit: (data: ClienteFormData) => void;
  isLoading?: boolean;
}

export function ClienteFormDialog({
  open,
  onOpenChange,
  mode,
  cliente,
  onSubmit,
  isLoading = false,
}: ClienteFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClienteFormData>({
    resolver: zodResolver(clienteSchema),
    defaultValues: {
      ativo: true,
    },
  });

  const ativo = watch("ativo");

  useEffect(() => {
    if (open) {
      if (mode === "edit" && cliente) {
        reset({
          nome: cliente.nome,
          cpf_cnpj: cliente.cpf_cnpj || "",
          telefone: cliente.telefone || "",
          email: cliente.email || "",
          endereco: cliente.endereco || "",
          data_nascimento: cliente.data_nascimento || "",
          observacoes: cliente.observacoes || "",
          ativo: cliente.ativo,
        });
      } else {
        reset({
          nome: "",
          cpf_cnpj: "",
          telefone: "",
          email: "",
          endereco: "",
          data_nascimento: "",
          observacoes: "",
          ativo: true,
        });
      }
    }
  }, [open, mode, cliente, reset]);

  const handleFormSubmit = (data: ClienteFormData) => {
    onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Cliente" : "Editar Cliente"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input id="nome" {...register("nome")} />
              {errors.nome && (
                <p className="text-sm text-destructive mt-1">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
              <Input id="cpf_cnpj" {...register("cpf_cnpj")} />
              {errors.cpf_cnpj && (
                <p className="text-sm text-destructive mt-1">{errors.cpf_cnpj.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input id="telefone" {...register("telefone")} placeholder="(00) 00000-0000" />
              {errors.telefone && (
                <p className="text-sm text-destructive mt-1">{errors.telefone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...register("email")} />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="data_nascimento">Data de Nascimento</Label>
              <Input id="data_nascimento" type="date" {...register("data_nascimento")} />
              {errors.data_nascimento && (
                <p className="text-sm text-destructive mt-1">{errors.data_nascimento.message}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="endereco">Endereço</Label>
              <Input id="endereco" {...register("endereco")} />
              {errors.endereco && (
                <p className="text-sm text-destructive mt-1">{errors.endereco.message}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea id="observacoes" {...register("observacoes")} rows={3} />
              {errors.observacoes && (
                <p className="text-sm text-destructive mt-1">{errors.observacoes.message}</p>
              )}
            </div>

            <div className="col-span-2 flex items-center space-x-2">
              <Switch
                id="ativo"
                checked={ativo}
                onCheckedChange={(checked) => setValue("ativo", checked)}
              />
              <Label htmlFor="ativo">Cliente Ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
